/**
 * Dead-man-switch. Configure ping cadence + grace + secondary verifier.
 * On enable, generates a 32-byte master key, Shamir-shards 3-of-5,
 * wraps each share with nominee hint, stores in vault.
 *
 * Pure-client v0: there is no remote ping endpoint. The vault tracks
 * last_ping_at locally. On overdue + grace breach, the UI exposes a
 * release URL the secondary verifier can forward to nominees.
 *
 * v0.2 will wire a PocketBase relay so pings work without opening the app.
 */
import { store } from '../store.ts';
import { withVault, injectCommonStyles } from './_shared.ts';
import type { NomineeId } from '../types.ts';
import { split } from '../chassis/shamir.ts';
import { wrapShare } from '../chassis/crypto.ts';

const TPL = /* html */ `
<style>
.dms {
  display: grid;
  gap: 16px;
}
.dms-status {
  display: grid;
  gap: 10px;
  padding: 14px;
  border: 1px solid var(--rule-strong);
  border-radius: 2px;
  background: var(--parchment);
}
.dms-status.overdue { border-color: var(--crimson); background: oklch(48% 0.180 25 / 0.06); }
.dms-status.ok { border-color: oklch(48% 0.110 145 / 0.40); background: oklch(48% 0.110 145 / 0.04); }
.dms-line {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 10px;
  align-items: baseline;
  font-family: var(--mono);
  font-size: 11px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--ink-dim);
}
.dms-line b { color: var(--ink); font-family: var(--display); font-style: italic; font-size: 14px; letter-spacing: 0.02em; text-transform: none; font-weight: 600; }
.cadence-row { display: flex; gap: 8px; flex-wrap: wrap; }
.cadence-chip {
  font-family: var(--mono);
  font-size: 9.5px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  padding: 6px 12px;
  background: var(--parchment);
  border: 1px solid var(--rule-strong);
  border-radius: 99px;
  cursor: pointer;
  user-select: none;
}
.cadence-chip.active { background: var(--indigo); color: var(--parchment); border-color: var(--indigo); }
.warn {
  font-family: var(--display);
  font-style: italic;
  font-size: 14px;
  color: var(--crimson);
  margin-top: 4px;
}
.shamir-table { font-family: var(--mono); font-size: 10px; color: var(--ink-dim); line-height: 1.6; padding: 12px 14px; background: var(--parchment); border: 1px solid var(--rule-hair); }
</style>
<section class="frame">
  <h2>Dead-man-switch <span class="deva">मृत्यु-कुंजी</span></h2>
  <div class="kicker">Periodic ping · Shamir 3-of-5 · grace before release</div>
  <div class="dms" id="dms"></div>
</section>
`;

export class WasiyatDeadManSwitch extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #unsub: (() => void) | null = null;

  connectedCallback(): void {
    injectCommonStyles(this.#shadow);
    this.#shadow.innerHTML += TPL;
    this.#unsub = withVault(this, (v, root) => {
      const mount = root.getElementById('dms');
      if (!mount) return;
      const dms = v.dead_man_switch;
      const shamirHolders = v.nominees.filter((n) => n.is_shamir_holder);
      const eligible = shamirHolders.length >= dms.shamir_total;
      const now = Date.now();
      const last = new Date(dms.last_ping_at).getTime();
      const daysSince = Math.floor((now - last) / 86_400_000);
      const overdue = dms.enabled && daysSince > dms.ping_interval_days;
      const overdueBy = overdue ? daysSince - dms.ping_interval_days : 0;
      const inGrace = overdue && overdueBy <= dms.grace_period_days;

      let statusCls = '';
      let statusLine = 'Switch is off';
      if (dms.enabled && !overdue) {
        statusCls = 'ok';
        statusLine = `Active · last ping ${daysSince} day${daysSince === 1 ? '' : 's'} ago`;
      } else if (dms.enabled && inGrace) {
        statusCls = 'overdue';
        statusLine = `Overdue · ${overdueBy} day${overdueBy === 1 ? '' : 's'} into grace period`;
      } else if (dms.enabled && overdue) {
        statusCls = 'overdue';
        statusLine = `Release imminent · grace expired ${overdueBy - dms.grace_period_days} day${(overdueBy - dms.grace_period_days) === 1 ? '' : 's'} ago`;
      }

      mount.innerHTML = `
        <div class="dms-status ${statusCls}">
          <div class="dms-line">
            <span>status</span>
            <b>${statusLine}</b>
            <span>${dms.enabled ? 'ON' : 'OFF'}</span>
          </div>
          <div class="dms-line">
            <span>cadence</span>
            <b>${dms.ping_interval_days} days</b>
            <span>grace ${dms.grace_period_days}d</span>
          </div>
          <div class="dms-line">
            <span>shamir</span>
            <b>${dms.shamir_threshold}-of-${dms.shamir_total}</b>
            <span>${shamirHolders.length}/${dms.shamir_total} holders</span>
          </div>
        </div>
        ${!eligible ? `<div class="warn">Mark ${dms.shamir_total - shamirHolders.length} more nominee(s) as Shamir holder(s) above to enable.</div>` : ''}
        <div class="kicker">Cadence</div>
        <div class="cadence-row" id="cadence">
          ${([30, 60, 90, 180, 365] as const).map((d) => `<span class="cadence-chip ${d === dms.ping_interval_days ? 'active' : ''}" data-d="${d}">${d} days</span>`).join('')}
        </div>
        <div class="btn-row">
          <button class="btn" id="ping-btn">I'm well · ping now</button>
          <button class="btn ${dms.enabled ? 'danger' : ''}" id="toggle-btn" ${!eligible && !dms.enabled ? 'disabled' : ''}>${dms.enabled ? 'Disable switch' : 'Enable switch'}</button>
        </div>
        ${dms.enabled ? `<div class="shamir-table">
          Shamir holders, in release order:<br/>
          ${shamirHolders.map((n, i) => `${i + 1}. ${n.display_name} (P${n.contact_priority})`).join('<br/>') || '<i>(none assigned yet)</i>'}<br/><br/>
          On release: wasiyat.yourtube.in/release/{vault-id}<br/>
          Any 3 of the 5 holders can reconstruct the master key.
        </div>` : ''}
      `;

      root.querySelectorAll<HTMLElement>('.cadence-chip').forEach((c) => {
        c.addEventListener('click', () => {
          const d = Number(c.dataset['d']) as 30 | 60 | 90 | 180 | 365;
          void store.mutate((draft) => { draft.dead_man_switch.ping_interval_days = d; });
        });
      });

      const pingBtn = root.getElementById('ping-btn');
      pingBtn?.addEventListener('click', () => {
        void store.mutate((draft) => { draft.dead_man_switch.last_ping_at = new Date().toISOString(); });
      });

      const toggleBtn = root.getElementById('toggle-btn');
      toggleBtn?.addEventListener('click', async () => {
        if (dms.enabled) {
          await store.mutate((draft) => { draft.dead_man_switch.enabled = false; draft.dead_man_switch.release_status = 'idle'; });
        } else {
          if (shamirHolders.length < dms.shamir_total) return;
          // Generate master key + Shamir + wrap per holder. Stored in DMS itself
          // is only metadata; the wrapped shares attach to crypto_seeds and are
          // regenerated whenever a new crypto seed is added.
          await store.mutate((draft) => {
            draft.dead_man_switch.enabled = true;
            draft.dead_man_switch.last_ping_at = new Date().toISOString();
            draft.dead_man_switch.release_status = 'pending_ping';
            const secondary = shamirHolders[0]?.id ?? null;
            if (secondary) draft.dead_man_switch.secondary_verifier_id = secondary as NomineeId;
          });
          await regenerateAllShares();
        }
      });
    });
  }

  disconnectedCallback(): void { this.#unsub?.(); }
}

async function regenerateAllShares(): Promise<void> {
  const s = store.state;
  if (!s.vault) return;
  const holders = s.vault.nominees.filter((n) => n.is_shamir_holder);
  if (holders.length < s.vault.dead_man_switch.shamir_total) return;
  for (const seed of s.vault.crypto_seeds) {
    const masterKey = new Uint8Array(seed.shamir_shares_encrypted.length > 0
      ? 32 // master key was generated; cannot re-derive from existing shares without decrypt. Skip.
      : 32);
    if (seed.shamir_shares_encrypted.length > 0) continue;
    crypto.getRandomValues(masterKey);
    const shares = split(masterKey, s.vault.dead_man_switch.shamir_threshold, s.vault.dead_man_switch.shamir_total);
    const wrapped = await Promise.all(
      shares.map((sh, i) => wrapShare(sh, holders[i]?.id ?? `holder-${i}`))
    );
    await store.mutate((draft) => {
      const target = draft.crypto_seeds.find((cs) => cs.id === seed.id);
      if (target) target.shamir_shares_encrypted = wrapped;
    });
  }
}

customElements.define('wasiyat-dead-man-switch', WasiyatDeadManSwitch);
